import { trackAnalyticsEvent } from '@autone/analytics';
import { useGetUserConfigQuery } from '@autone/redux/core';
import { useAutoneTranslation } from '@autone/translations';
import {
  cn,
  CoreLocationClustersMultiselect,
  CoreLocationMultiselect,
  CoreMultiselectWrapper,
  CoreProductMultiselect,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
  type Option,
  ProductUploadPopover,
  SERVER_MIN_SEARCH_TERM_LENGTH,
  SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
  TailwindButton,
  TailwindForm,
} from '@autone/ui';
import { Button } from '@autone/ui-next/atoms';
import { DownloadIcon } from '@autone/ui-next/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence } from 'motion/react';
import { useCallback, useState } from 'react';
import {
  useController,
  useForm,
  useFormState,
  useWatch,
} from 'react-hook-form';

import {
  type APP_NAME,
  PRODUCT_UPLOAD_POPOVER_ANALYTICS_EVENTS,
} from '../../../consts';
import {
  useAssortmentTemplateExport,
  useGetAssortmentTemplateExportRowCount,
} from '../../../hooks';
import { AggregationTypeRadioToggleGroup } from '../aggregation-type-radio-toggle-group/AggregationTypeRadioToggleGroup';
import { ModalFooterLoadingIndicator } from '../modal-footer-loading-indicator';

import { CoreLocationCountBadge } from './components/CoreLocationsCountBadge';
import { CoreProductsCountBadge } from './components/CoreProductsCountBadge';
import { ExportLimitExceededBanner } from './components/ExportLimitExceededBanner';
import {
  locationClustersFiltersApiToFormMap,
  locationFiltersApiToFormMap,
  productFiltersApiToFormMap,
  productGroupsFiltersApiToFormMap,
} from './DownloadTemplateModal.helpers';
import {
  CoreDropdownGridLayout,
  CoreDropdownSectionHeaderLayout,
} from './DownloadTemplateModal.layouts';
import {
  type DownloadAssortmentTemplateFormSchema,
  downloadAssortmentTemplateFormSchema,
} from './DownloadTemplateModal.schema';

export const DownloadTemplateModal = () => {
  const { common } = useAutoneTranslation<typeof APP_NAME>();
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          id="download-template"
          aria-label={common('download-template')}
          variant="secondary"
        >
          <DownloadIcon />
          {common('download-template')}
        </Button>
      </DialogTrigger>
      <DownloadTemplateModalContent />
    </Dialog>
  );
};

export const DownloadTemplateModalContent = () => {
  const { t, common } = useAutoneTranslation<typeof APP_NAME>();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [formIsFocused, setFormIsFocused] = useState(false);
  const { data: { multi_brand: isMultiBrand } = {} } = useGetUserConfigQuery(
    {},
  );

  const form = useForm({
    resolver: zodResolver(downloadAssortmentTemplateFormSchema),
    mode: 'onChange',
    defaultValues: {
      aggregationType: 'product_location',
      filters: {
        geographic: {
          regions: null,
          countries: null,
          locations: null,
          locationTypes: null,
        },
        product: {
          departments: null,
          subDepartments: null,
          collectionTypes: null,
          seasons: null,
          events: null,
          brands: null,
          manufacturers: null,
          classes: null,
          subClasses: null,
          productGroups: null,
          productIds: null,
        },
        locationClusters: null,
      },
    },
  });
  const exportRowCountResults = useGetAssortmentTemplateExportRowCount(
    form.control,
  );

  const {
    exportRowCount: exportRowCount,
    exportRowCountLowerLimit: exportRowCountLowerLimit,
    exportRowCountUpperLimit: exportRowCountUpperLimit,
    isLoading,
  } = exportRowCountResults;

  const exportRowCountIsValid =
    exportRowCount !== null &&
    (exportRowCountUpperLimit === null ||
      exportRowCount <= exportRowCountUpperLimit) &&
    (exportRowCountLowerLimit === null ||
      exportRowCount >= exportRowCountLowerLimit);

  const { control, handleSubmit } = form;
  const aggregationType = useWatch({ control, name: 'aggregationType' });
  const {
    field: { onChange: onProductIdsChange },
  } = useController({ control, name: 'filters.product.productIds' });

  const {
    downloadTemplateExport,
    cancelDownload,
    exportIsLoading,
    exportIsSuccess,
  } = useAssortmentTemplateExport({
    onExportReady: (url) => setDownloadUrl(url),
    exportRowCount,
  });

  const handleExportTemplate = useCallback(
    async ({
      aggregationType,
      filters,
    }: DownloadAssortmentTemplateFormSchema) => {
      const { geographic, product, locationClusters } = filters;
      trackAnalyticsEvent({
        category: 'assortment_template_import_export',
        action: 'download_template_clicked',
        properties: {
          aggregation_type: aggregationType,
        },
      });

      const { locationTypes, regions, countries, locations } = geographic;
      const {
        departments,
        subDepartments,
        collectionTypes,
        seasons,
        events,
        brands,
        manufacturers,
        classes,
        subClasses,
        productGroups,
        productIds,
      } = product;

      downloadTemplateExport({
        body: {
          aggregation_type: aggregationType,
          filters: {
            product: {
              product_groups: productGroups,
              departments: departments,
              sub_departments: subDepartments,
              collection_types: collectionTypes,
              seasons: seasons,
              events: events,
              brands: brands,
              manufacturers: manufacturers,
              classes: classes,
              sub_classes: subClasses,
              product_ids: productIds,
            },
            geographic: {
              location_types: locationTypes,
              regions: regions,
              countries: countries,
              locations: locations,
            },
            location_cluster: {
              location_clusters: locationClusters,
            },
          },
        },
      });
    },
    [downloadTemplateExport],
  );

  const handleDownloadTemplate = useCallback(() => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
      setDownloadUrl(null);
    }
  }, [downloadUrl]);

  const handleProductCsvUpload = useCallback(
    (products: Option[]) => {
      const productIds = products.map(({ id }) => id);
      onProductIdsChange(productIds);
    },
    [onProductIdsChange],
  );

  const { isValid } = useFormState({ control: form.control });

  const shouldRenderExportLoader = exportIsLoading || downloadUrl !== null;
  const shouldRenderExportRowCountOutOfBounds =
    !isLoading &&
    !exportRowCountIsValid &&
    exportRowCount !== null &&
    !!exportRowCountUpperLimit &&
    !!exportRowCountLowerLimit;

  const disabled =
    !isValid || shouldRenderExportRowCountOutOfBounds || exportIsLoading;

  return (
    <DialogContent
      className="flex flex-col gap-0 p-0 max-w-[1000px] w-[800px] max-h-[90vh] "
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <DialogTitle className="p-6 border-b border-b-grey-10">
        {t('download-template-modal.modal-header')}
      </DialogTitle>

      <TailwindForm {...form}>
        <form
          data-testid={'download-template-form'}
          className={cn(
            'relative flex flex-col',
            formIsFocused ? 'overflow-y-hidden' : 'overflow-y-scroll',
          )}
          onFocus={() => setFormIsFocused(true)}
          onBlur={() => setFormIsFocused(false)}
        >
          <div className="flex flex-col p-6 gap-10 grow">
            {/** AGGREGATION TYPE SELECTION */}
            <div className="flex flex-col gap-2 grow">
              <p className="text-base font-medium">
                {t('download-template-modal.granularity-section-header')}
              </p>
              <AggregationTypeRadioToggleGroup
                onFocus={(e) => e.stopPropagation()}
                control={form.control}
                name={'aggregationType'}
              />
            </div>

            {/** LOCATION CLUSTER SELECTION */}
            <div className="flex flex-col gap-4 grow">
              <CoreDropdownSectionHeaderLayout
                headerText={t.withCustomer(
                  'download-template-modal.location-cluster-scope.$section-header',
                )}
              />

              <CoreDropdownGridLayout>
                <CoreMultiselectWrapper
                  label={common.withCustomer('filters.$location-clusters')}
                >
                  <CoreLocationClustersMultiselect
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.locationClusters"
                    locationsMap={locationFiltersApiToFormMap}
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:location_clusters_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>
              </CoreDropdownGridLayout>
            </div>

            {/** LOCATION GROUP SELECTION */}
            <div className="flex flex-col gap-4 grow">
              <CoreDropdownSectionHeaderLayout
                headerText={t(
                  'download-template-modal.location-scope.section-header',
                )}
                badge={<CoreLocationCountBadge />}
              />

              <CoreDropdownGridLayout>
                {/** location types */}
                <CoreMultiselectWrapper label={common('location-types')}>
                  <CoreLocationMultiselect.LocationTypes
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.geographic.locationTypes"
                    locationsMap={locationFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:location_types_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** regions */}
                <CoreMultiselectWrapper label={common.withCustomer('$regions')}>
                  <CoreLocationMultiselect.Regions
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.geographic.regions"
                    locationsMap={locationFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action: 'download_template_modal:regions_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** countries */}
                <CoreMultiselectWrapper label={common('countries')}>
                  <CoreLocationMultiselect.Countries
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.geographic.countries"
                    locationsMap={locationFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:countries_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** locations */}
                <CoreMultiselectWrapper
                  label={common('locations')}
                  infoText={common('dropdowns.results-limit-tooltip-text', {
                    resultsLimit: SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
                    searchTermLengthLimit: SERVER_MIN_SEARCH_TERM_LENGTH,
                  })}
                >
                  <CoreLocationMultiselect.Locations
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.geographic.locations"
                    locationsMap={locationFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:locations_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>
              </CoreDropdownGridLayout>
            </div>

            {/** PRODUCT GROUP SELECTION */}
            <div className="flex flex-col gap-4 grow">
              <CoreDropdownSectionHeaderLayout
                headerText={t(
                  'download-template-modal.product-scope.section-header',
                )}
                badge={<CoreProductsCountBadge />}
              />

              <CoreDropdownGridLayout>
                {/** product groups */}
                <CoreMultiselectWrapper
                  label={common.withCustomer('$product-groups')}
                >
                  <CoreProductMultiselect.ProductGroups
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.productGroups"
                    productMap={productFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:product_groups_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** collection types */}
                <CoreMultiselectWrapper
                  label={common('scope.collection-types')}
                >
                  <CoreProductMultiselect.CollectionTypes
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.collectionTypes"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:collection_types_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** departments */}
                <CoreMultiselectWrapper
                  label={common.withCustomer('$departments')}
                >
                  <CoreProductMultiselect.Departments
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.departments"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:departments_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** sub-departments */}
                <CoreMultiselectWrapper
                  label={common.withCustomer('$sub-departments')}
                >
                  <CoreProductMultiselect.SubDepartments
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.subDepartments"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:sub_departments_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** seasons */}
                <CoreMultiselectWrapper label={common('seasons')}>
                  <CoreProductMultiselect.Seasons
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.seasons"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action: 'download_template_modal:seasons_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** events */}
                <CoreMultiselectWrapper
                  label={common.withCustomer('filters.$events')}
                >
                  <CoreProductMultiselect.Events
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.events"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action: 'download_template_modal:events_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                <CoreMultiselectWrapper label={common('manufacturers')}>
                  <CoreProductMultiselect.Manufacturers
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.manufacturers"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:manufacturers_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                <CoreMultiselectWrapper
                  label={common.withCustomer('scope.$classes')}
                >
                  <CoreProductMultiselect.Classes
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.classes"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action: 'download_template_modal:classes_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                <CoreMultiselectWrapper
                  label={common.withCustomer('scope.$sub-classes')}
                >
                  <CoreProductMultiselect.SubClasses
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.subClasses"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:sub_classes_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>

                {/** brands */}
                {isMultiBrand && (
                  <CoreMultiselectWrapper label={common('brands')}>
                    <CoreProductMultiselect.Brands
                      disabled={exportIsLoading}
                      control={control}
                      name="filters.product.brands"
                      productMap={productFiltersApiToFormMap}
                      productGroupsMap={productGroupsFiltersApiToFormMap}
                      locationClustersMap={locationClustersFiltersApiToFormMap}
                      analyticsEvent={{
                        category: 'assortment_template_import_export',
                        action:
                          'download_template_modal:brands_filter_selected',
                      }}
                    />
                  </CoreMultiselectWrapper>
                )}

                {/** products */}
                <CoreMultiselectWrapper
                  label={common('products')}
                  infoText={common('dropdowns.results-limit-tooltip-text', {
                    resultsLimit: SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
                    searchTermLengthLimit: SERVER_MIN_SEARCH_TERM_LENGTH,
                  })}
                  labelIcon={
                    <ProductUploadPopover
                      onValidUpload={handleProductCsvUpload}
                      analyticsEvents={PRODUCT_UPLOAD_POPOVER_ANALYTICS_EVENTS}
                    />
                  }
                >
                  <CoreProductMultiselect.Products
                    disabled={exportIsLoading}
                    control={control}
                    name="filters.product.productIds"
                    productMap={productFiltersApiToFormMap}
                    productGroupsMap={productGroupsFiltersApiToFormMap}
                    locationClustersMap={locationClustersFiltersApiToFormMap}
                    analyticsEvent={{
                      category: 'assortment_template_import_export',
                      action:
                        'download_template_modal:products_filter_selected',
                    }}
                  />
                </CoreMultiselectWrapper>
              </CoreDropdownGridLayout>
            </div>
          </div>
          <AnimatePresence>
            {shouldRenderExportRowCountOutOfBounds && (
              <ExportLimitExceededBanner
                key="export-limit-exceeded-banner"
                aggregationType={aggregationType}
                exportRowCount={exportRowCount}
                exportRowCountUpperLimit={exportRowCountUpperLimit}
                exportRowCountLowerLimit={exportRowCountLowerLimit}
              />
            )}
          </AnimatePresence>
        </form>
      </TailwindForm>
      <DialogFooter className="p-6 border-t border-t-grey-10 z-50">
        {shouldRenderExportLoader && (
          <ModalFooterLoadingIndicator
            complete={exportIsSuccess}
            onCompleteAnimationEnd={handleDownloadTemplate}
            loadingText={t('download-template-modal.generating-your-export')}
            completeText={t('download-template-modal.export-ready')}
          />
        )}
        <DialogClose asChild>
          <TailwindButton
            variant={'outlined'}
            color={'secondary'}
            onClick={(e) => {
              if (exportIsLoading) {
                e.preventDefault();
                cancelDownload();
              }
            }}
          >
            {common('buttons.cancel')}
          </TailwindButton>
        </DialogClose>
        <TailwindButton
          onClick={handleSubmit(handleExportTemplate)}
          type="submit"
          variant={'contained'}
          color={'primary'}
          disabled={disabled}
          loading={exportIsLoading}
        >
          {common('download-template')}
        </TailwindButton>
      </DialogFooter>
    </DialogContent>
  );
};
